import { Box, Radio, Typography } from "@outschool/backpack";
import {
  PublicReviewFragmentFragment,
  RespondToReviewMutation,
  RespondToReviewMutationVariables,
  SendMessageToUserMutation,
  SendMessageToUserMutationVariables
} from "@outschool/gql-frontend-generated";
// This file is not yet translated.
/* eslint-disable i18next/no-literal-string */
import { gql, useMutation } from "@outschool/ui-apollo";
import { PublicReviewFragment } from "@outschool/ui-components-website";
import {
  ModalActionButtons,
  TextArea
} from "@outschool/ui-legacy-component-library";
import React from "react";

import * as Env from "../../../shared/Env";
import * as Routes from "../../../shared/Routes";
import ActionType from "../../actions/ActionType";
import { useUserMode } from "../../lib/UserMode";
import { useAppState } from "../../stores/AppStateProvider";
import ErrorMessage from "../ErrorMessage";
import Link from "../Link";
// @ts-ignore - JSX import
import { sendMessageMutation } from "../MessageModal";
import PageModal from "../PageModal";

const respondToReviewMutation = gql`
  mutation respondToReview($feedbackUid: ID!, $response: String!) {
    respondToReview(feedbackUid: $feedbackUid, response: $response) {
      ...PublicReviewFragment
    }
  }
  ${PublicReviewFragment}
`;

type ReviewResponseModalProps = {
  activity: PublicReviewFragmentFragment["activity"];
  review: PublicReviewFragmentFragment;
  onClose?: () => void;
};

export const ReviewResponseModal: React.FunctionComponent<
  ReviewResponseModalProps
> = ({ activity, review, onClose }) => {
  const appState = useAppState();
  const userMode = useUserMode();

  const [isPrivateMessage, setIsPrivateMessage] = React.useState(true);
  const [message, setMessage] = React.useState("");
  // @ts-ignore TS(2345): Argument of type 'null' is not assignable to param... Remove this comment to see the full error message
  const [errorMessage, setErrorMessage] = React.useState<string>(null);

  const close = React.useCallback(() => {
    setMessage("");
    onClose && onClose();
  }, [onClose, setMessage]);

  const handleMessageChange = React.useCallback(
    (e: $TSFixMe) => setMessage(e.target.value),
    [setMessage]
  );

  const handlePrivateMessageChange = React.useCallback(() => {
    setIsPrivateMessage(!isPrivateMessage);
  }, [setIsPrivateMessage, isPrivateMessage]);

  const [sendPrivateMessage] = useMutation<
    SendMessageToUserMutation,
    SendMessageToUserMutationVariables
  >(sendMessageMutation);

  const [respondToReview] = useMutation<
    RespondToReviewMutation,
    RespondToReviewMutationVariables
  >(respondToReviewMutation);

  const submitPrivateMessage = async (content: string) => {
    try {
      const {
        // @ts-ignore TS(2339): Property 'sendMessage' does not exist on type 'Sen... Remove this comment to see the full error message
        data: { sendMessage: message }
      } = await sendPrivateMessage({
        variables: {
          recipientUids: [review.parent.uid],
          messageInput: {
            content,
            // @ts-ignore TS(2531): Object is possibly 'null'.
            activityUid: activity.uid,
            sectionUid: review.section_uid,
            details: {
              action: "Review response",
              section: Routes.sectionAttendeesUrl(review.section_uid),
              // @ts-ignore TS(2531): Object is possibly 'null'.
              activityTitle: activity.title,
              // @ts-ignore TS(2531): Object is possibly 'null'.
              subject: `Responding to review on ${activity.title}`
            }
          }
        }
      });
      close(); // Close before setting top notice
      appState.appDispatcher.dispatch(ActionType.Webapp.SET_TOP_NOTICE, {
        message: (
          <React.Fragment>
            <span>Your private response was sent.</span>{" "}
            {message.thread_uid && (
              <span>
                View your conversation{" "}
                <Link
                  to={Routes.websiteRoutes.adultThreadPathV2(
                    userMode,
                    message.thread_uid
                  )}
                >
                  here
                </Link>
                .
              </span>
            )}
          </React.Fragment>
        )
      });
    } catch (e) {
      setErrorMessage(e);
    }
  };

  const submitPublicResponse = async (response: string) => {
    await respondToReview({
      variables: {
        feedbackUid: review.uid,
        response
      }
    });
    close();
  };

  const handleSubmit = async () => {
    const trimmedMessage = message.trim();

    if (!trimmedMessage) {
      setErrorMessage("Please write a response");
      return;
    }

    if (isPrivateMessage) {
      await submitPrivateMessage(trimmedMessage);
    } else {
      await submitPublicResponse(trimmedMessage);
    }
  };

  return (
    <PageModal open onClose={close}>
      <ErrorMessage
        value={errorMessage}
        showStatusPageLink={Env.IS_READ_ONLY_MODE}
      />
      <form
        onSubmit={e => {
          e.preventDefault();
          handleSubmit();
        }}
      >
        <div>
          <Typography
            variant="h3"
            sx={{
              marginBottom: "1em",
              marginRight: "1em"
            }}
          >
            Respond to review
          </Typography>
          <pre>
            If you have concerns about a parent's review, please discuss
            privately before posting a public reponse.
          </pre>
          <TextArea
            placeholder={`Write your ${
              isPrivateMessage ? "private message" : "public response"
            } here`}
            showCount={false}
            rows={4}
            maxRows={7}
            value={message}
            onChange={handleMessageChange}
          />
          <Box
            flex
            sx={{
              whiteSpace: "nowrap"
            }}
          >
            <label
              style={{
                marginRight: "1em"
              }}
            >
              <Radio
                checked={isPrivateMessage}
                onChange={handlePrivateMessageChange}
                style={{
                  marginRight: "0.5em"
                }}
              />
              Private message
            </label>
            <label
              style={{
                marginRight: "1em"
              }}
            >
              <Radio
                checked={!isPrivateMessage}
                onChange={handlePrivateMessageChange}
                style={{
                  marginRight: "0.5em"
                }}
              />
              Public response
            </label>
          </Box>
        </div>
        <ModalActionButtons
          cancelLabel="Cancel"
          onCancel={close}
          primaryLabel="Submit"
          primaryType="submit"
        />
      </form>
    </PageModal>
  );
};
